import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { VesselSkin } from '../shared/utils/vessel-skin';

/**
 * Unified map aggregation engine — a direct port of the Flutter app's
 * `_computeWaypointClusters()` / `_mergeNearClusters()`
 * (`lib/features/home_screen/presentation/waypoints/home_screen_waypoint_render.dart`).
 *
 * Mobile is the source of truth for this feature. The web previously ran THREE
 * overlapping engines (a screen-pixel vessel grid, a lat/lng waypoint grid, and
 * a "mixed cluster" glue layer that stitched their outputs back together). That
 * split is why production counts never reconciled: a vessel could be counted by
 * the vessel grid AND again by a mixed bubble, waypoint bubbles excluded vessels
 * from their badge entirely, and the two grids used different cell sizes, merge
 * distances and zoom cut-offs. Zooming in therefore never summed back to the
 * number shown when zoomed out.
 *
 * This engine restores mobile's model: ONE grid, every entity bucketed exactly
 * once, `count = waypointsInCell + distinctVesselPositionsInCell`, then a single
 * union-find merge pass (transitive, as on mobile). Total across all bubbles is
 * conserved by construction.
 */

// ─── Inputs ───────────────────────────────────────────────────────────────────

/** A waypoint eligible for aggregation (event + alert types only, like mobile). */
export interface ClusterableWaypoint {
  /** Stable waypoint id — used to suppress the individual pin. */
  id: string;
  lat: number;
  lng: number;
  /** Resolved icon URL used for the bubble's preview pins. */
  iconUrl: string;
  /** True when the waypoint belongs to the signed-in user (mobile's `_dot` variant). */
  isMine?: boolean;
}

/**
 * A vessel eligible for aggregation. The user's own/primary vessel must be
 * filtered out before it reaches this engine — mobile never clusters it
 * (`isOwn` skip in `_computeWaypointClusters`).
 */
export interface ClusterableVessel {
  /** Unique vessel id */
  id: string;
  /** Geographic latitude */
  lat: number;
  /** Geographic longitude */
  lng: number;
  /** Heading in degrees (0-360) or null */
  heading: number | null;
  /** Speed over ground in knots or null */
  speed: number | null;
  /** Whether this is the current user's primary vessel */
  isPrimary: boolean;
  /** Course over ground for dockmate vessels */
  cog: number | null;
  /** ISO timestamp of last observation */
  lastSeen: string | null;
  /** Hull art from `boat_skin`; null/absent = not learned yet (never defaulted). */
  skin?: VesselSkin | null;
  /** Any additional data the consumer wants to carry through */
  metadata?: any;
}

// ─── Output ───────────────────────────────────────────────────────────────────

/** One aggregated bubble. Mirrors Flutter's `WaypointCluster`. */
export interface MapCluster {
  /** Stable membership-derived id (same members ⇒ same id across recomputes). */
  id: string;
  centerLat: number;
  centerLng: number;
  /**
   * Badge count — waypoints PLUS distinct vessel positions, exactly like mobile.
   * Summing `count` over every rendered bubble (plus the individually rendered
   * markers) always equals the total number of entities on the map.
   */
  count: number;
  waypointCount: number;
  vesselCount: number;
  /** Up to 3 preview pin icons (waypoints only — the vessel is the watermark). */
  previewIcons: string[];
  /** Whether a vessel was absorbed → draw the faded boat watermark. */
  hasVessel: boolean;
  /** Ids of the waypoints absorbed here (their individual pins must hide). */
  waypointIds: string[];
  /** Ids of the vessels absorbed here (their individual boats must hide). */
  vesselIds: string[];
  /**
   * Hull every absorbed vessel agrees on, else null (mixed or not yet learned →
   * generic boat). Mirrors Flutter's `WaypointCluster.vesselSkin`.
   */
  vesselSkin: VesselSkin | null;
}

export interface MapClusterResult {
  /** Every cluster the grid produced, including count-1 cells. */
  clusters: MapCluster[];
  /**
   * The subset that actually renders as a bubble. Mirrors Flutter's
   * `pureCluster` rule: below {@link MapClusteringService.CLUSTER_ZOOM_THRESHOLD}
   * EVERY cell is a bubble (even a count-1 cell); at/above it only count ≥ 2.
   * Because the two thresholds are equal and `compute` returns nothing at/above
   * them, in practice this is every cluster.
   */
  renderedClusters: MapCluster[];
  /** Waypoint ids absorbed by a RENDERED bubble — hide their individual pins. */
  hiddenWaypointIds: Set<string>;
  /** Vessel ids absorbed by a RENDERED bubble — hide their individual boats. */
  hiddenVesselIds: Set<string>;
  /** The zoom this result was computed at. */
  zoom: number;
}

// ─── Internals ────────────────────────────────────────────────────────────────

interface Cell {
  waypoints: ClusterableWaypoint[];
  /** dedupKey -> {lat, lng} for distinct vessel POSITIONS in this cell. */
  vesselPoints: Map<string, { lat: number; lng: number }>;
  /** Every vessel id that landed here, including position duplicates. */
  vesselIds: string[];
  /** Distinct KNOWN hulls; unknown vessels contribute nothing (cannot outvote). */
  vesselSkins: Set<VesselSkin>;
}

const EMPTY_RESULT: MapClusterResult = {
  clusters: [],
  renderedClusters: [],
  hiddenWaypointIds: new Set<string>(),
  hiddenVesselIds: new Set<string>(),
  zoom: 0,
};

@Injectable({ providedIn: 'root' })
export class MapClusteringService {
  /**
   * Below this zoom mobile renders a PURE cluster view: every grid cell becomes
   * a bubble, even a cell holding a single item, and no individual pin or boat
   * is drawn. (Flutter `_clusterZoomThreshold`.)
   */
  static readonly CLUSTER_ZOOM_THRESHOLD = 8.0;

  /**
   * At/above this zoom aggregation is disabled entirely — everything renders
   * individually regardless of proximity. (Flutter `_noClusterZoomThreshold`.)
   *
   * It deliberately MEETS {@link CLUSTER_ZOOM_THRESHOLD}, exactly as the two
   * Dart constants do. Mobile has NO hybrid band: below the value you get
   * bubbles and nothing else, at/above it you get individual pins and boats and
   * nothing else, and the handover happens at one single zoom level.
   *
   * Mobile lowered both constants 11 → 10 → 8 on purpose ("bubbles were forming
   * almost as soon as the map left close range"); web now matches the current
   * Dart values. Re-check `home_screen_waypoint_render.dart` before tuning.
   *
   * Move BOTH together if this is ever tuned again — a gap between them leaves a
   * band where an item renders as neither bubble nor pin.
   */
  static readonly NO_CLUSTER_ZOOM = 8.0;

  /**
   * MapLibre's camera zoom uses the 512px-tile convention. The old web code used
   * 256, which made every screen-pixel cell span TWICE the intended number of
   * degrees — markers up to ~2× farther apart merged, producing the inflated
   * low-zoom badges (the "over 200 at one zoom" report) that could never be
   * reproduced by summing the bubbles after zooming in.
   */
  private static readonly TILE_SIZE = 512;

  /** Screen-pixel grid cell. A marker is ~44px, so both buckets sit above it. */
  private static cellPx(zoom: number): number {
    return zoom < MapClusteringService.CLUSTER_ZOOM_THRESHOLD ? 84.0 : 52.0;
  }

  /** Post-merge "these two bubbles visually touch" distance, in screen pixels. */
  private static mergeDistPx(zoom: number): number {
    return zoom < MapClusteringService.CLUSTER_ZOOM_THRESHOLD ? 56.0 : 40.0;
  }

  /** Degrees spanned by one grid cell at this zoom. */
  static cellDeg(zoom: number): number {
    return (
      (MapClusteringService.cellPx(zoom) * 360.0) /
      (MapClusteringService.TILE_SIZE * Math.pow(2, zoom))
    );
  }

  /** True when aggregation runs at all at this zoom. */
  static isClusteringActive(zoom: number): boolean {
    return zoom < MapClusteringService.NO_CLUSTER_ZOOM;
  }

  /** Mobile clamps tap-to-expand at zoom 18. */
  getClusterClickZoom(currentZoom: number): number {
    return Math.min(18, Math.max(0, currentZoom + 2));
  }

  /** Badge label — mobile caps at 99+. */
  formatCountBadge(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  // ── Result stream (data-driven recomputes) ─────────────────────────────────

  private pendingRaf: number | null = null;
  private _lastResult: MapClusterResult | null = null;
  private readonly _result$ = new Subject<MapClusterResult>();
  readonly result$: Observable<MapClusterResult> = this._result$.asObservable();

  getLastResult(): MapClusterResult | null {
    return this._lastResult;
  }

  /** Synchronous compute that also publishes the result as the latest. */
  computeAndStore(
    waypoints: ClusterableWaypoint[],
    vessels: ClusterableVessel[],
    zoom: number,
  ): MapClusterResult {
    const result = this.compute(waypoints, vessels, zoom);
    this._lastResult = result;
    return result;
  }

  /** Coalesce bursts of data updates into one recompute per animation frame. */
  scheduleRecompute(
    waypoints: ClusterableWaypoint[],
    vessels: ClusterableVessel[],
    zoom: number,
  ): void {
    if (this.pendingRaf !== null) cancelAnimationFrame(this.pendingRaf);
    this.pendingRaf = requestAnimationFrame(() => {
      this.pendingRaf = null;
      this._result$.next(this.computeAndStore(waypoints, vessels, zoom));
    });
  }

  reset(): void {
    if (this.pendingRaf !== null) {
      cancelAnimationFrame(this.pendingRaf);
      this.pendingRaf = null;
    }
    this._lastResult = null;
  }

  // ── Core ────────────────────────────────────────────────────────────────────

  compute(
    waypoints: ClusterableWaypoint[],
    vessels: ClusterableVessel[],
    zoom: number,
  ): MapClusterResult {
    if (!Number.isFinite(zoom) || zoom >= MapClusteringService.NO_CLUSTER_ZOOM) {
      return { ...EMPTY_RESULT, zoom };
    }

    const cellDeg = MapClusteringService.cellDeg(zoom);
    if (!Number.isFinite(cellDeg) || cellDeg <= 0) {
      return { ...EMPTY_RESULT, zoom };
    }

    const cells = new Map<string, Cell>();
    const cellFor = (lat: number, lng: number): Cell => {
      const col = Math.floor(lng / cellDeg);
      const row = Math.floor(lat / cellDeg);
      const key = `${row},${col}`;
      let cell = cells.get(key);
      if (!cell) {
        cell = { waypoints: [], vesselPoints: new Map(), vesselIds: [], vesselSkins: new Set() };
        cells.set(key, cell);
      }
      return cell;
    };

    // ── Waypoints ─────────────────────────────────────────────────────────────
    for (const wp of waypoints) {
      if (!this.isValidPosition(wp.lat, wp.lng)) continue;
      cellFor(wp.lat, wp.lng).waypoints.push(wp);
    }

    // ── Vessels ───────────────────────────────────────────────────────────────
    // Mobile dedupes by exact position across its two live-position feeds, so a
    // vessel arriving on both paths is counted once. We key on 6dp (~0.1 m) since
    // JS float identity is fragile. Crucially, EVERY id sharing that position is
    // recorded so all of them get suppressed — the old code dropped duplicates
    // outright, which left a ghost boat floating on top of its own bubble.
    for (const v of vessels) {
      if (v.isPrimary) continue; // own boat never aggregates (mobile `isOwn`)
      if (!this.isValidPosition(v.lat, v.lng)) continue;
      const cell = cellFor(v.lat, v.lng);
      const dedupKey = `${v.lat.toFixed(6)},${v.lng.toFixed(6)}`;
      if (!cell.vesselPoints.has(dedupKey)) {
        cell.vesselPoints.set(dedupKey, { lat: v.lat, lng: v.lng });
      }
      cell.vesselIds.push(v.id);
      // Unknown skin contributes nothing: one unlearned boat must not outvote the rest.
      if (v.skin) cell.vesselSkins.add(v.skin);
    }

    if (cells.size === 0) return { ...EMPTY_RESULT, zoom };

    // ── Cell → cluster ────────────────────────────────────────────────────────
    const raw: MapCluster[] = [];
    for (const cell of cells.values()) {
      const wpCount = cell.waypoints.length;
      const vesselPts = Array.from(cell.vesselPoints.values());
      if (wpCount === 0 && vesselPts.length === 0) continue;

      // Vessel positions contribute to the centre only when the cell holds no
      // waypoints (mobile keeps bubbles anchored on the pins when both exist).
      let sumLat = 0;
      let sumLng = 0;
      let posCount = 0;
      for (const wp of cell.waypoints) {
        sumLat += wp.lat;
        sumLng += wp.lng;
        posCount++;
      }
      if (posCount === 0) {
        for (const pt of vesselPts) {
          sumLat += pt.lat;
          sumLng += pt.lng;
          posCount++;
        }
      }

      raw.push({
        id: '',
        centerLat: posCount > 0 ? sumLat / posCount : 0,
        centerLng: posCount > 0 ? sumLng / posCount : 0,
        count: wpCount + vesselPts.length,
        waypointCount: wpCount,
        vesselCount: vesselPts.length,
        previewIcons: cell.waypoints.slice(0, 3).map((w) => w.iconUrl || ''),
        hasVessel: vesselPts.length > 0,
        waypointIds: cell.waypoints.map((w) => w.id).filter(Boolean),
        vesselIds: cell.vesselIds.slice(),
        // Exactly one distinct known hull ⇒ the bubble can show it.
        vesselSkin: cell.vesselSkins.size === 1 ? [...cell.vesselSkins][0] : null,
      });
    }

    const merged = this.mergeNearClusters(raw, cellDeg, MapClusteringService.cellPx(zoom), MapClusteringService.mergeDistPx(zoom));
    for (const c of merged) c.id = this.membershipId(c);

    return this.buildResult(merged, zoom);
  }

  // ── Post-merge pass ─────────────────────────────────────────────────────────

  /**
   * Union-find merge, identical in outcome to Flutter's `_mergeNearClusters`:
   * every pair of cluster centres within `touchDistPx` screen pixels is joined,
   * and joins are transitive (A~B and B~C ⇒ one bubble).
   *
   * Flutter compares every pair (O(n²)). Here candidates come from a uniform
   * grid sized at the merge distance — any pair within the threshold shares a
   * 3×3 bucket neighbourhood — so the union set, and therefore the result, is
   * the same while the scan stays near-linear for thousands of cells.
   */
  private mergeNearClusters(
    clusters: MapCluster[],
    cellDeg: number,
    cellPx: number,
    touchDistPx: number,
  ): MapCluster[] {
    const n = clusters.length;
    if (n <= 1) return clusters;

    const degPerPx = cellDeg / cellPx;
    const touchDeg = touchDistPx * degPerPx;
    const touchDegSq = touchDeg * touchDeg;

    const buckets = new Map<string, number[]>();
    for (let i = 0; i < n; i++) {
      const key = `${Math.floor(clusters[i].centerLat / touchDeg)},${Math.floor(clusters[i].centerLng / touchDeg)}`;
      const bucket = buckets.get(key);
      if (bucket) bucket.push(i);
      else buckets.set(key, [i]);
    }

    // Path-compressed union-find.
    const parent = Array.from({ length: n }, (_, i) => i);
    const find = (x: number): number => {
      while (parent[x] !== x) {
        parent[x] = parent[parent[x]];
        x = parent[x];
      }
      return x;
    };

    for (let i = 0; i < n; i++) {
      const a = clusters[i];
      const by = Math.floor(a.centerLat / touchDeg);
      const bx = Math.floor(a.centerLng / touchDeg);
      for (let gy = by - 1; gy <= by + 1; gy++) {
        for (let gx = bx - 1; gx <= bx + 1; gx++) {
          const bucket = buckets.get(`${gy},${gx}`);
          if (!bucket) continue;
          for (const j of bucket) {
            if (j <= i) continue;
            const b = clusters[j];
            const dLat = a.centerLat - b.centerLat;
            const dLng = a.centerLng - b.centerLng;
            if (dLat * dLat + dLng * dLng <= touchDegSq) parent[find(i)] = find(j);
          }
        }
      }
    }

    // Group by root, keeping ascending member index (Flutter's preview order).
    const groups = new Map<number, number[]>();
    for (let i = 0; i < n; i++) {
      const root = find(i);
      const g = groups.get(root);
      if (g) g.push(i);
      else groups.set(root, [i]);
    }

    const out: MapCluster[] = [];
    for (const indices of groups.values()) {
      if (indices.length === 1) {
        out.push(clusters[indices[0]]);
        continue;
      }
      let totalCount = 0;
      let waypointCount = 0;
      let vesselCount = 0;
      let sumLat = 0;
      let sumLng = 0;
      let hasVessel = false;
      let anyUnknownSkin = false;
      const skins = new Set<VesselSkin>();
      const previewIcons: string[] = [];
      const waypointIds: string[] = [];
      const vesselIds: string[] = [];
      for (const i of indices) {
        const c = clusters[i];
        totalCount += c.count;
        waypointCount += c.waypointCount;
        vesselCount += c.vesselCount;
        // Count-weighted centroid, matching mobile.
        sumLat += c.centerLat * c.count;
        sumLng += c.centerLng * c.count;
        hasVessel = hasVessel || c.hasVessel;
        if (c.vesselSkin) skins.add(c.vesselSkin);
        // A vessel-bearing part with no agreed skin leaves the answer unknown.
        if (c.hasVessel && !c.vesselSkin) anyUnknownSkin = true;
        for (const icon of c.previewIcons) if (previewIcons.length < 3) previewIcons.push(icon);
        waypointIds.push(...c.waypointIds);
        vesselIds.push(...c.vesselIds);
      }
      out.push({
        id: '',
        centerLat: totalCount > 0 ? sumLat / totalCount : clusters[indices[0]].centerLat,
        centerLng: totalCount > 0 ? sumLng / totalCount : clusters[indices[0]].centerLng,
        count: totalCount,
        waypointCount,
        vesselCount,
        previewIcons,
        hasVessel,
        waypointIds,
        vesselIds,
        // Re-applied over the union: two unanimous bubbles can merge into a mixed one.
        vesselSkin: skins.size === 1 && !anyUnknownSkin ? [...skins][0] : null,
      });
    }
    return out;
  }

  // ── Render/visibility rules ────────────────────────────────────────────────

  /**
   * Flutter's `pureCluster` rule: below the cluster threshold every cell is
   * drawn as a bubble (a lone boat or pin wears a count-less bubble); at/above
   * it only dense (count ≥ 2) clusters are. The set drawn here and the set
   * hidden from the individual-marker layers are two halves of ONE decision, so
   * both derive from `renderedClusters`.
   */
  private buildResult(clusters: MapCluster[], zoom: number): MapClusterResult {
    const pureCluster = zoom < MapClusteringService.CLUSTER_ZOOM_THRESHOLD;
    const renderedClusters = pureCluster ? clusters : clusters.filter((c) => c.count >= 2);

    const hiddenWaypointIds = new Set<string>();
    const hiddenVesselIds = new Set<string>();
    for (const c of renderedClusters) {
      for (const id of c.waypointIds) hiddenWaypointIds.add(id);
      for (const id of c.vesselIds) hiddenVesselIds.add(id);
    }

    return { clusters, renderedClusters, hiddenWaypointIds, hiddenVesselIds, zoom };
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  private isValidPosition(lat: number, lng: number): boolean {
    return (
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  }

  /**
   * Membership-derived id: identical members always produce the same id, so the
   * renderer reuses (and merely repositions) the existing DOM marker instead of
   * destroying and re-creating it — which is what made bubbles flicker on pan.
   */
  private membershipId(c: MapCluster): string {
    const wp = c.waypointIds.slice().sort().join(',');
    const vs = c.vesselIds.slice().sort().join(',');
    return `cl:${wp}::${vs}`;
  }
}
